import {MITTWALD_API, Schemas} from '../middleware/MittwaldApi'
import * as ActionTypes from '../constants/ActionTypes'
import {CART_CHECKOUT_REQUEST, CART_REMOVE_ITEM_SUCCESS} from '../constants/ActionTypes'

import {putFormData, putSimpleFormData} from "./FormData";
import {queryBearerToken} from "./UserAuthentication";
import {showPopupWithIframe} from "./Popup";
import {queryPromotionCodeIfArticleInCode, queryPromotionCodeStatusAndData} from "./PromotionCode";
import {_forcePartnerId, getEndpointURL, isNoRedirect} from "../selectors/GetParam";
import {getDomainInCartObject} from "../selectors/CartUtil";
import {getItemInCartByArticleNumber, getMainArticleInCart} from "../selectors/Cart";
import {getArticleOptionsData, getCartId, getCartItems} from "../selectors/SimpleSelectors";
import {APP_MODE_OUTSOURCING} from "../constants/General";
import {isMode} from "../selectors/Mode";
import {getEngine} from "../util/engine";
import {cartApiEndpoint} from "../config/Config";
import {isExistingUserAuthenticatedAndSelected} from "../selectors/UserInterface";

const itemsOperationQueue = [];
let itemOperationRunning = false;
let itemOperationPromise = null;

const processItemOperation = (dispatch, getState, resolve) => {
    itemOperationRunning = true;
    const operation = itemsOperationQueue.shift();
    dispatch(operation[0].call(null, ...operation[1])).then(args => {
        if (itemsOperationQueue.length > 0) {
            setTimeout(() => {
                processItemOperation(dispatch, getState, resolve);
            }, 50)
        } else {
            itemOperationRunning = false;
            itemOperationPromise = null;
            setTimeout(() => {
                resolve(true);
            }, 50)
        }
    });
}

/**
 * wrapper function to execute promises in sequence
 * @param queuedOperation
 * @param queuedParams
 * @returns {(function(*, *): (null))|*}
 */
export const queueItemOperation = (queuedOperation, ...queuedParams) => (dispatch, getState) => {
    itemsOperationQueue.push([queuedOperation, queuedParams]);
    if (!itemOperationRunning) {
        itemOperationPromise = new Promise((resolve, reject) => {
            processItemOperation(dispatch, getState, resolve)
        });
    }
    if (itemOperationPromise) {
        return itemOperationPromise
    }
}

// **************************************************************************************************************
const fetchCartShare = (bearer, cartId) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_SHARE_REQUEST, ActionTypes.CART_SHARE_SUCCESS, ActionTypes.CART_SHARE_FAILURE],
        endpoint: `${cartApiEndpoint}/${cartId}/actions/share`,
        bearer: bearer,
        schema: Schemas.CARTSHARESCHEMA,
        method: 'POST'
    }
})

export const queryCartShare = (bearer = null) => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchCartShare(bearer ? bearer : getState().token.bearer, getState().cart.id))
    })
}

// **************************************************************************************************************
const fetchNewCartFromTemplate = (bearer, presetId) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_CREATE_REQUEST, ActionTypes.CART_CREATE_SUCCESS, ActionTypes.CART_CREATE_FAILURE],
        endpoint: `${cartApiEndpoint}/${presetId}`,
        bearer: bearer,
        schema: Schemas.CARTSCHEMA,
        method: 'POST',
        payload: {
            createdFromPreset: presetId
        }
    }
})

export const queryNewCartFromTemplate = (presetId) => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchNewCartFromTemplate(getState().token.bearer, presetId))
    })
}

// **************************************************************************************************************
const fetchNewCart = (bearer) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_CREATE_REQUEST, ActionTypes.CART_CREATE_SUCCESS, ActionTypes.CART_CREATE_FAILURE],
        endpoint: `${cartApiEndpoint}`,
        bearer: bearer,
        schema: Schemas.CARTSCHEMA,
        method: 'POST',
        postData: isMode(APP_MODE_OUTSOURCING) ? {
            forcePartnerId: _forcePartnerId
        } : null
    }
})

export const queryNewCart = () => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchNewCart(getState().token.bearer))
    })
}

// **************************************************************************************************************
const fetchCartUpdate = (bearer, cartId, payload = {}) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_UPDATE_REQUEST, ActionTypes.CART_UPDATE_SUCCESS, ActionTypes.CART_UPDATE_FAILURE],
        endpoint: `${cartApiEndpoint}/${cartId}`,
        bearer: bearer,
        schema: Schemas.CARTSCHEMA,
        method: 'GET',
        payload: payload
    }
})

export const queryPreloadedCart = (cartId) => (dispatch, getState) => {

    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchCartUpdate(getState().token.bearer, cartId, {updateSteps: true, isUserAuthenticated: getState().clientDataStep.clientData !== false}))
    })

}

export const queryCartUpdate = () => (dispatch, getState) => {

    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchCartUpdate(getState().token.bearer, getCartId(getState())))
    })

}

// **************************************************************************************************************
const fetchAddItemToCart = (bearer, cartId, articleNumber) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_ADD_ITEM_REQUEST, ActionTypes.CART_ADD_ITEM_SUCCESS, ActionTypes.CART_ADD_ITEM_FAILURE],
        endpoint: `${cartApiEndpoint}/${cartId}/items`,
        bearer: bearer,
        schema: Schemas.CARTSCHEMA,
        method: 'POST',
        payload: {
            itemId: articleNumber
        },
        postData: {
            identifier: articleNumber
        }
    }
})

export const addItemToCart = (articleNumber) => (dispatch, getState) => {
    if (!articleNumber) {
        return Promise.resolve();
    }
    return dispatch(queryBearerToken()).then(() => {
        // show popup when item added to cart
        const articleData = getArticleOptionsData(getState())[articleNumber]
        const onSelectPopup = articleData ? articleData.onSelectPopup : false

        if (onSelectPopup) {
            setTimeout(() => {
                dispatch(showPopupWithIframe(onSelectPopup))
            }, 500)
        }
        return dispatch(fetchAddItemToCart(getState().token.bearer, getCartId(getState()), "" + articleNumber)).then(action => {
            return dispatch(queryPromotionCodeIfArticleInCode("" + articleNumber))
        })
    })

}


export const setInitialParam = (key, value) => {
    return {
        type: ActionTypes.CART_SET_INITIAL,
        key: key,
        value: value
    }
}

// **************************************************************************************************************
const fetchRemoveItemFromCart = (bearer, cartId, itemId, itemType = "", isSwapping = false) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_REMOVE_ITEM_REQUEST, ActionTypes.CART_REMOVE_ITEM_SUCCESS, ActionTypes.CART_REMOVE_ITEM_FAILURE],
        endpoint: `${cartApiEndpoint}/${cartId}/items/${itemId}`,
        bearer: bearer,
        schema: Schemas.CARTSCHEMA,
        method: 'DELETE',
        payload: {
            itemType: itemType,
            itemId: itemId,
            isSwapping: isSwapping
        }
    }
})

export const removeItemFromCart = (itemId) => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchRemoveItemFromCart(getState().token.bearer, getCartId(getState()), itemId)).then(action => {
            if (action.type === CART_REMOVE_ITEM_SUCCESS) {
                return dispatch(queryPromotionCodeStatusAndData())
            } else {
                // try a second time
                return dispatch(fetchRemoveItemFromCart(getState().token.bearer, getCartId(getState()), itemId)).then(action => {
                    return dispatch(queryPromotionCodeStatusAndData())
                });
            }
        })
    })
}

// todo: failsafe remove calls by adding them to queue, for now a second attempt will be made
export const removeItemFromCartByArticleNumber = (articleNumber, isSwapping) => (dispatch, getState) => {

    const itemId = getItemInCartByArticleNumber(getState(), articleNumber)

    if (!itemId) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(true);
            }, 50);
        })
    }

    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchRemoveItemFromCart(getState().token.bearer, getCartId(getState()), itemId, '', isSwapping)).then(action => {
            if (action.type === CART_REMOVE_ITEM_SUCCESS) {
                return dispatch(queryPromotionCodeIfArticleInCode(articleNumber))
            } else {
                // try a second time
                return dispatch(fetchRemoveItemFromCart(getState().token.bearer, getCartId(getState()), itemId, '', isSwapping)).then(action => {
                    return dispatch(queryPromotionCodeIfArticleInCode(articleNumber))
                });
            }
        })
    })

}

export const removeDomainFromCart = (itemId) => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchRemoveItemFromCart(getState().token.bearer, getCartId(getState()), itemId, 'DOMAIN'))
    })
}
// **************************************************************************************************************
const fetchAddDomainToCart = (bearer, cartId, domainNameAndEnding) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_ADD_DOMAIN_REQUEST, ActionTypes.CART_ADD_DOMAIN_SUCCESS, ActionTypes.CART_ADD_DOMAIN_FAILURE],
        endpoint: `${cartApiEndpoint}/${cartId}/domains`,
        bearer: bearer,
        schema: Schemas.STATUSTEXT,
        method: 'POST',
        payload: {
            domain: domainNameAndEnding
        },
        postData: {
            name: domainNameAndEnding
        }
    }
})

export const addDomainToCart = (domainNameAndEnding) => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(fetchAddDomainToCart(getState().token.bearer, getCartId(getState()), domainNameAndEnding))
    })
}

// dummy action until api is fixed
export const addDomainToCartAndRefresh = (domainNameAndEnding) => (dispatch, getState) => {
    Promise.all(
        [dispatch(addDomainToCart(domainNameAndEnding))]
    ).then((args) => {
            dispatch(queryCartUpdate())
        }
    )
}

export const removeAllDomainsFromCart = () => (dispatch, getState) => {
    const domainItems = getDomainInCartObject(getState());

    domainItems.forEach((domainObject) => {
        dispatch(queueItemOperation(removeItemFromCart, domainObject.itemId));
    })
}

export const validateCartPaidSoftware = () => (dispatch, getState) => {


}

// **************************************************************************************************************
const fetchCartCheckout = (bearer, cartId, pinCode) => ({
    [MITTWALD_API]: {
        types: [ActionTypes.CART_CHECKOUT_REQUEST, ActionTypes.CART_CHECKOUT_SUCCESS, ActionTypes.CART_CHECKOUT_FAILURE],
        endpoint: `${cartApiEndpoint}/${cartId}/actions/checkout`,
        bearer: bearer,
        schema: Schemas.STATUSCODE,
        method: 'POST',
        postData: pinCode ? {
            pinCode
        } : null
    }
})

export const queryCartCheckout = () => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(putFormData(true)).then(args => {

            return dispatch(fetchCartCheckout(getState().token.bearer, getCartId(getState()), getState().properties.pinCode)).then(args => {


                if (args && args.type === ActionTypes.CART_CHECKOUT_SUCCESS) {
                    const state = getState();
                    const engine = getEngine();
                    engine.save({});

                    if (isNoRedirect()) {
                        console.log("no redirect detected");
                    } else {
                        setTimeout(() => {
                            performFinalRedirect(state);
                        }, 350)
                    }
                } else {
                    alert("Bei der Bestellung ist leider ein Problem aufgetreten. Bei weiteren Fragen wende dich bitte an den Kundenservice.")
                }
            })
        })
    })
}

export const querySimpleCartCheckout = () => (dispatch, getState) => {
    return dispatch(queryBearerToken()).then(() => {
        return dispatch(putSimpleFormData()).then(args => {
            return dispatch(fetchCartCheckout(getState().token.bearer, getCartId(getState()))).then(args => {

                const state = getState();
                const endpointURL = getEndpointURL();
                const engine = getEngine();

                engine.save({});

                if (args && args.type === ActionTypes.CART_CHECKOUT_SUCCESS) {
                    setTimeout(() => {
                        if (endpointURL) {
                            top.location.assign(endpointURL)
                        } else {
                            performFinalRedirect(state, top);
                        }
                    }, 350)
                } else {
                    alert("Bei der Bestellung ist leider ein Problem aufgetreten. Bei weiteren Fragen wende dich bitte an den Kundenservice.")
                }
            })
        })
    })
}

const performFinalRedirect = (state, target = window) => {
    const isAuthenticated = isExistingUserAuthenticatedAndSelected(state);
    const mainArticle = getMainArticleInCart(state);
    const articleData = getArticleOptionsData(state);
    const mainArticleData = articleData[mainArticle];

    let url;
    if (mainArticleData) {
        url = `https://www.mittwald.de/bestellung/bestellbestaetigung?articlenumber=${mainArticleData.articleNumber}&articletitle=${mainArticleData.title}&new=${isAuthenticated ? 'false' : 'true'}`
    } else {
        url = 'https://www.mittwald.de/bestellung/bestellbestaetigung'
    }

    target.location.assign(url);
}


export const queryCartRemoveAllItems = () => (dispatch, getState) => {
    const itemsInCart = getCartItems(getState());
    let promise = null;
    itemsInCart.forEach((item) => {
        promise = dispatch(queueItemOperation(removeItemFromCart, item.id));
    })
    return itemOperationPromise || promise || Promise.resolve(true);
}



/** WEBPACK FOOTER **
 ** ./src/actions/Cart.js
 **/